import { useState, useCallback } from 'react';
import { submitFreeformFeedback, submitSpecificSkillsFeedback } from '../services/api';

function useFeedbackWorkflow({ showNotification }) {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackPromptSeen, setFeedbackPromptSeen] = useState(false);
  const [feedbackContext, setFeedbackContext] = useState('freeform');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackLikertResponse, setFeedbackLikertResponse] = useState('');
  const [feedbackSkillPracticeResponse, setFeedbackSkillPracticeResponse] = useState('');
  const [feedbackResistanceResponse, setFeedbackResistanceResponse] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  const countWords = useCallback((value) => {
    return value.trim() ? value.trim().split(/\s+/).filter(Boolean).length : 0;
  }, []);

  const feedbackWordCount = countWords(feedbackText);

  const resetFeedbackForm = useCallback(() => {
    setFeedbackText('');
    setFeedbackLikertResponse('');
    setFeedbackSkillPracticeResponse('');
    setFeedbackResistanceResponse('');
    setFeedbackSubmitting(false);
  }, []);

  const openFeedbackDialog = useCallback((context) => {
    resetFeedbackForm();
    setFeedbackContext(context);
    setFeedbackDialogOpen(true);
  }, [resetFeedbackForm]);

  const closeFeedbackDialog = useCallback(() => {
    setFeedbackDialogOpen(false);
  }, []);

  const promptForCompletedSession = useCallback((selectedProfile) => {
    if (selectedProfile === -2 || selectedProfile === -3 || selectedProfile === -4 || feedbackPromptSeen) {
      return;
    }

    setFeedbackContext('freeform');
    resetFeedbackForm();
    setFeedbackDialogOpen(true);
    setFeedbackPromptSeen(true);
  }, [feedbackPromptSeen, resetFeedbackForm]);

  const handleFeedbackSubmit = useCallback(async () => {
    if (feedbackSubmitting) {
      return;
    }

    setFeedbackSubmitting(true);
    try {
      const payload = {
        q1Score: feedbackLikertResponse,
        q2Score: feedbackSkillPracticeResponse,
        q3Score: feedbackResistanceResponse,
        feedback: feedbackText.trim(),
      };

      if (feedbackContext === 'specific-skills') {
        await submitSpecificSkillsFeedback(payload);
      } else {
        await submitFreeformFeedback(payload);
      }

      setFeedbackDialogOpen(false);
      resetFeedbackForm();
    } catch (error) {
      const message = error?.response?.data?.error || 'Failed to submit feedback. Please try again.';
      showNotification(message);
    } finally {
      setFeedbackSubmitting(false);
    }
  }, [
    feedbackLikertResponse,
    feedbackResistanceResponse,
    feedbackSkillPracticeResponse,
    feedbackContext,
    feedbackSubmitting,
    feedbackText,
    resetFeedbackForm,
    showNotification,
  ]);

  return {
    feedbackDialogOpen,
    feedbackContext,
    feedbackLikertResponse,
    setFeedbackLikertResponse,
    feedbackSkillPracticeResponse,
    setFeedbackSkillPracticeResponse,
    feedbackResistanceResponse,
    setFeedbackResistanceResponse,
    feedbackWordCount,
    feedbackText,
    setFeedbackText,
    countWords,
    feedbackSubmitting,
    openFeedbackDialog,
    closeFeedbackDialog,
    promptForCompletedSession,
    handleFeedbackSubmit,
  };
}

export default useFeedbackWorkflow;
